import compose from 'recompose/compose';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Accordion  from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ProductFilters from 'models/ProductFilters';
import PropTypes from 'prop-types';
import React from 'react';
import toJS from 'shared/components/toJS';
import Typography from '@material-ui/core/Typography';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { actions as productFilterActions } from 'redux/domain/ProductFilter';
import { withTheme } from '@material-ui/core/styles';
import PriceFilter from '../components/PriceFilter';

class PriceFilterContainer extends React.Component {
	constructor() {
		super();

		this.decimalRegex = /^\d*\.?\d{0,2}$/;
		this.state = { priceError: '' };
		this.handleMaxPriceTextChange = this.handleMaxPriceTextChange.bind(this);
		this.handleMinPriceTextChange = this.handleMinPriceTextChange.bind(this);
		this.handlePriceFilterApply = this.handlePriceFilterApply.bind(this);
		this.getBorderStyle = this.getBorderStyle.bind(this);
	}

	/**
	 * Determines if a min price or a max price have been entered as part
	 * of the current active filter. If so it returns a css border object
	 * to be used on the container, showing that the price filter is active.
	 */
	getBorderStyle() {
		const { theme: { palette: { secondary: { main } } } } = this.props;
		return this.props.filters.minPrice !== 0 || this.props.filters.maxPrice != null
			? { border: `solid ${main} 5px` }
			: {};
	}

	/**
	 * Update the text currently displayed in the min price textbox.
	 * @param {string} value The number to display in the textbox. Should be either a positive integer or an empty string.
	 */
	handleMinPriceTextChange(value) {
		if (this.decimalRegex.test(value)) {
			this.props.setMinPriceText(value);
		}
	}

	/**
	 * Update the text currently displayed in the max price textbox.
	 * @param {string} value The number to display in the textbox. Should be either a positive integer or an empty string.
	 */
	handleMaxPriceTextChange(value) {
		if (this.decimalRegex.test(value)) {
			this.props.setMaxPriceText(value);
		}
	}

	/**
	 * Apply the values from the price textboxes to the filters
	 */
	handlePriceFilterApply() {
		const { minPriceText, maxPriceText } = this.props;
		const minPrice = minPriceText !== '' ? Number.parseFloat(minPriceText) : 0; // If min price field is blank, set minPrice to 0
		const maxPrice = maxPriceText !== '' ? Number.parseFloat(maxPriceText) : undefined; // If max price field is blank, set maxPrice to undefined
		if (maxPrice !== undefined && minPrice > maxPrice) {
			this.setState({ priceError: 'Min should be less than Max' });
		} else {
			this.setState({ priceError: '' });
			this.props.handleValueChange({ minPrice, maxPrice });
		}
	}

	render() {
		const { priceError } = this.state;
		const { minPriceText, maxPriceText } = this.props;
		return (
			<Accordion style={this.getBorderStyle()}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography variant="subtitle2">Price Per Item</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<PriceFilter
						minPriceText={minPriceText}
						maxPriceText={maxPriceText}
						handleMinPriceTextChange={this.handleMinPriceTextChange}
						handleMaxPriceTextChange={this.handleMaxPriceTextChange}
						handlePriceFilterApply={this.handlePriceFilterApply}
						priceError={priceError}
					/>
				</AccordionDetails>
			</Accordion>
		);
	}
}

PriceFilterContainer.propTypes = {
	filters: PropTypes.instanceOf(ProductFilters),
	handleValueChange: PropTypes.func.isRequired,
	maxPriceText: PropTypes.string,
	minPriceText: PropTypes.string,
	setMaxPriceText: PropTypes.func.isRequired,
	setMinPriceText: PropTypes.func.isRequired,
	theme: PropTypes.instanceOf(Object).isRequired,
};

PriceFilterContainer.defaultProps = {
	filters: {},
	maxPriceText: '',
	minPriceText: '',
};

const mapStateToProps = state => {
	const filters = state.productFilter.get('filters');
	const maxPriceText = state.productFilter.get('maxPriceText');
	const minPriceText = state.productFilter.get('minPriceText');
	return {
		filters,
		maxPriceText,
		minPriceText,
	};
};

const mapDispatchToProps = dispatch => bindActionCreators(productFilterActions, dispatch);

// for testing purposes
export { PriceFilterContainer as PriceFilterContainerClass, mapStateToProps };

export default compose(connect(mapStateToProps, mapDispatchToProps), withTheme)(
	toJS(PriceFilterContainer),
);
